/** For an app whose own configuration is what failed. */
export const CONFIGURATION_HINT = 'Check the Entra client ID, tenant, and redirect URI for this app.';

/** For everything else — a failure the configuration did not cause and editing it will not fix. */
export const NEUTRAL_HINT = 'Reload the page to try again. If it keeps happening, report the message above.';

/**
 * Discovery failures that come from the authority being wrong rather than from anything later. A wrong
 * tenant cannot produce a discovery document (`openid_config_error`); the subdomain authority produced
 * one whose issuer MSAL refused, reported as `endpoints_resolution_error` (see `buildExternalConfig`).
 */
const DISCOVERY_FAILURES = new Set(['endpoints_resolution_error', 'openid_config_error']);

/**
 * Which line goes under `AuthProvider`'s "Sign-in is unavailable": the configuration hint only for an
 * error that genuinely means the app is misconfigured — MSAL's own `ClientConfigurationError` (an
 * untrusted authority, a refused issuer, a malformed URL) or authority discovery failing.
 *
 * Everything else gets the neutral line, because the hint is an instruction and a wrong one costs an
 * afternoon: the staging popup of 2026-10-03 failed with `no_token_request_cache_error`, a flow problem,
 * and the screen sent its reader to check a client ID, tenant and redirect URI that were all correct.
 * `BrowserConfigurationAuthError` is deliberately *not* configuration — `storage_not_supported` is about
 * the browser, not this app.
 *
 * Read structurally (`name`, `errorCode`) rather than by `instanceof`, so it runs without MSAL.
 */
export function signInFailureHint(error: unknown): string {
  if (typeof error !== 'object' || error === null) return NEUTRAL_HINT;
  const { name, errorCode } = error as { name?: unknown; errorCode?: unknown };

  if (name === 'ClientConfigurationError') return CONFIGURATION_HINT;
  if (typeof errorCode === 'string' && DISCOVERY_FAILURES.has(errorCode)) return CONFIGURATION_HINT;
  return NEUTRAL_HINT;
}
